import React,{useEffect,useMemo,useState} from 'react';
import {
  ArrowLeft, Check, CheckCircle2, ChevronDown, ChevronRight, Database,
  Loader2, Search, X
} from 'lucide-react';
import MetricusMultiSelect from './ui/MetricusMultiSelect';
import MetricusInput from './ui/MetricusInput';
import MetricusSelect from './ui/MetricusSelect';
import MetricusDataGrid from './ui/MetricusDataGrid';
import OrganizationContextStep,{EMPTY_ORGANIZATION_CONTEXT} from './OrganizationContextStep';
import { createJiraRestSourceAdapter } from '../services/jiraRestSourceAdapter';

const MAX_CASES=50000;
const CORE_FIELD_IDS=new Set(['summary','created','creator','resolutiondate','project','issuetype']);
const RECOMMENDED_IDS=['priority','assignee','labels','components','resolution','reporter'];

const fmt=n=>Number(n||0).toLocaleString();
const defaultModel=initial=>({
  id:initial?.id||undefined,
  name:initial?.name||'Jira Process Model',
  category:initial?.category||'',
  processType:initial?.processType||'',
  description:initial?.description||'',
  businessObjective:initial?.source?.businessObjective||'',
  entityLabel:initial?.source?.entityLabel||'Issue',
});
const initialScope=initial=>({
  projectKeys:initial?.source?.scope?.projectKeys||[],
  issueTypeNames:initial?.source?.scope?.issueTypeNames||[],
  fromDate:initial?.source?.scope?.fromDate||'',
  toDate:initial?.source?.scope?.toDate||'',
  additionalJql:initial?.source?.scope?.additionalJql||'',
});

export default function JiraRestBuilder({repository,connectionId,connection,onBack,onRunProcessAdvisor,accessStatus=null,initial=null,initialStage='scope'}){
  const [stage,setStage]=useState(initialStage);
  const [metadata,setMetadata]=useState(null);
  const [scope,setScope]=useState(()=>initialScope(initial));
  const [showAdvancedScope,setShowAdvancedScope]=useState(()=>Boolean(
    initial?.source?.scope?.fromDate || initial?.source?.scope?.toDate || initial?.source?.scope?.additionalJql
  ));
  const [model,setModel]=useState(()=>defaultModel(initial));
  const [organizationContext,setOrganizationContext]=useState(()=>({...EMPTY_ORGANIZATION_CONTEXT,...(initial?.organizationContext||{})}));
  const [activityFieldId,setActivityFieldId]=useState(initial?.source?.activityFieldId||'status');
  const [selectedFieldIds,setSelectedFieldIds]=useState(()=>initial?.source?.selectedFieldIds||[]);
  const [attributeNotes,setAttributeNotes]=useState(()=>initial?.source?.attributeNotes||{});
  const [fieldSearch,setFieldSearch]=useState('');
  const [attributeView,setAttributeView]=useState('all');
  const [scopeCount,setScopeCount]=useState(()=>Number(initial?.summary?.configuredCases||initial?.summary?.requestedCases||initial?.summary?.cases||0)||null);
  const [scopeValidated,setScopeValidated]=useState(false);
  const [scopeEstimating,setScopeEstimating]=useState(false);
  const [scopeEstimateError,setScopeEstimateError]=useState('');
  const [jql,setJql]=useState(initial?.source?.jql||'');
  const [preview,setPreview]=useState([]);
  const [busy,setBusy]=useState('');
  const [error,setError]=useState('');
  const [retainProcessData,setRetainProcessData]=useState(Boolean(initial?.persistence?.retainProcessData ?? initial?.persistence?.storageConsent ?? initial?.source?.retainProcessData ?? initial?.source?.storageConsent ?? false));
  const [runAccessChoice,setRunAccessChoice]=useState('free');
  const [extractProgress,setExtractProgress]=useState({percent:0,status:'Preparing',step:'Starting',detail:'Preparing the Jira extraction.'});
  const adapter=useMemo(()=>createJiraRestSourceAdapter(repository,connectionId),[repository,connectionId]);

  useEffect(()=>{
    let alive=true;
    setBusy('metadata');setError('');
    adapter.getSourceMetadata().then(data=>{
      if(!alive)return;
      setMetadata(data);
      if(!initial?.source?.selectedFieldIds?.length){
        const available=new Set((data.fields||[]).map(f=>f.id));
        setSelectedFieldIds(RECOMMENDED_IDS.filter(id=>available.has(id)));
      }
    }).catch(e=>alive&&setError(e.message||'Unable to load Jira metadata')).finally(()=>alive&&setBusy(''));
    return()=>{alive=false};
  },[connectionId]);

  const projects=metadata?.projects||[];
  const issueTypes=useMemo(()=>{
    const map=new Map();
    for(const item of (metadata?.issueTypes||[]).filter(x=>!x.subtask)){
      if(!map.has(item.name))map.set(item.name,item);
    }
    return [...map.values()];
  },[metadata]);

  const projectOptions=useMemo(()=>
    projects.map(project=>({label:project.name||project.key,value:project.key,description:project.key})),
    [projects]
  );

  const issueTypeOptions=useMemo(()=>
    issueTypes.map(type=>({label:type.name,value:type.name,description:type.description||''})),
    [issueTypes]
  );

  const selectedProjectOptions=useMemo(()=>{
    const selected=new Set(scope.projectKeys);
    return projectOptions.filter(option=>selected.has(option.value));
  },[projectOptions,scope.projectKeys]);

  const selectedIssueTypeOptions=useMemo(()=>{
    const selected=new Set(scope.issueTypeNames);
    return issueTypeOptions.filter(option=>selected.has(option.value));
  },[issueTypeOptions,scope.issueTypeNames]);

  const activityField=useMemo(
    ()=>metadata?.fields?.find(f=>f.id===activityFieldId)||{id:activityFieldId,name:activityFieldId},
    [metadata,activityFieldId]
  );

  const attributeFields=useMemo(()=>{
    const selected=new Set(selectedFieldIds);
    const q=fieldSearch.trim().toLowerCase();
    return (metadata?.fields||[])
      .filter(f=>!CORE_FIELD_IDS.has(f.id)&&f.id!==activityFieldId)
      .filter(f=>attributeView==='all'||selected.has(f.id))
      .filter(f=>!q||`${f.name||''} ${f.id||''}`.toLowerCase().includes(q))
      .sort((a,b)=>{
        const sa=selected.has(a.id)?0:1,sb=selected.has(b.id)?0:1;
        return sa-sb||String(a.name).localeCompare(String(b.name));
      });
  },[metadata,fieldSearch,activityFieldId,selectedFieldIds,attributeView]);

  const selectedAttributeFields=useMemo(()=>{
    const byId=new Map((metadata?.fields||[]).map(f=>[f.id,f]));
    return selectedFieldIds.map(id=>byId.get(id)).filter(Boolean);
  },[metadata,selectedFieldIds]);

  const updateScope=next=>{
    setScope(next);
    setScopeValidated(false);
    setError('');
  };

  // Live count before validation. Debounced so project/type/date changes don't hammer Jira.
  useEffect(()=>{
    if(!metadata||stage!=='scope')return;
    let alive=true;
    const timer=setTimeout(async()=>{
      setScopeEstimating(true);setScopeEstimateError('');
      try{
        const result=await adapter.countScope(scope);
        if(!alive)return;
        setScopeCount(result.count);
        setJql(result.jql||'');
      }catch(e){
        if(!alive)return;
        setScopeCount(null);
        setScopeEstimateError(e.message||'Unable to estimate scope');
      }finally{if(alive)setScopeEstimating(false);}
    },450);
    return()=>{alive=false;clearTimeout(timer);};
  },[metadata,stage,scope,adapter]);

  useEffect(()=>{
    if(busy!=='extract')return;
    const start=Date.now();
    setExtractProgress({percent:2,status:'Preparing',step:'Starting',detail:'Preparing the selected Jira process model.'});
    const timer=setInterval(()=>{
      const elapsed=(Date.now()-start)/1000;
      setExtractProgress(prev=>{
        if(prev.percent>=94)return prev;
        const next=Math.min(94,Math.max(prev.percent+1,Math.round(4+elapsed*1.6)));
        let status='Preparing',step='Starting',detail='Preparing the selected Jira process model.';
        if(next>=12){status='Loading';step='Retrieving issues';detail=`Loading the authorized Jira cases for this run.`;}
        if(next>=35){status='Extracting';step='Reading activity history';detail='Retrieving Jira changelog history and reconstructing the activity sequence.';}
        if(next>=68){status='Processing';step='Building canonical model';detail='Converting Jira issues and history into the Process Advisor canonical model.';}
        if(next>=86){status='Validating';step='Final checks';detail='Validating cases, events and selected characteristics before opening Process Advisor.';}
        return {...prev,percent:next,status,step,detail};
      });
    },850);
    return()=>clearInterval(timer);
  },[busy,scopeCount]);

  const validateScope=async()=>{
    setBusy('count');setError('');
    try{
      const result=await adapter.countScope(scope);
      setScopeCount(result.count);setJql(result.jql);
      if(!result.withinLimit)throw new Error(`This scope contains approximately ${fmt(result.count)} cases. Reduce it to ${fmt(result.maxCases)} cases or fewer.`);
      if(result.count===0)throw new Error('The Jira scope contains no cases.');
      setScopeValidated(true);
      setStage('model');
    }catch(e){setError(e.message||'Unable to validate Jira scope');}
    finally{setBusy('');}
  };

  const toOrganization=()=>{
    if(!model.name.trim()){setError('Enter a model name.');return;}
    if(!model.processType.trim()){setError('Enter the process being analysed.');return;}
    setError('');setStage('organization');
  };

  const toggleAttribute=id=>{
    setSelectedFieldIds(current=>current.includes(id)?current.filter(x=>x!==id):[...current,id]);
  };

  const previewModel=async()=>{
    setBusy('preview');setError('');
    try{
      const result=await adapter.preview({jql,scope,activityFieldId,selectedFieldIds,limit:12});
      setPreview(result.issues||[]);setJql(result.jql||jql);setStage('preview');
    }catch(e){setError(e.message||'Unable to preview Jira data');}
    finally{setBusy('');}
  };

  const freeLimit=Number(accessStatus?.freeAssessment?.processingUnitLimit||accessStatus?.freeAssessment?.caseLimit||250);
  const availableAssessments=Number(accessStatus?.availableAssessments??accessStatus?.assessmentCredits?.balance??0);
  const sourceSubscription=(accessStatus?.subscriptions||[]).find(s=>s?.active&&String(s?.sourceConnectionId||'')===String(connectionId||''));
  const initialAssessmentActive=['assessment','assessment_credit'].includes(String(initial?.currentAccess?.source||initial?.accessSource||''));
  const paidBySubscription=Boolean(accessStatus?.subscription?.active);
  const paidByAssessment=false;
  const hasPaidAccess=paidBySubscription;
  const visibleProcessingLimit=hasPaidAccess?Number(scopeCount||0):Math.min(Number(scopeCount||0),freeLimit);
  const partialRun=Number(scopeCount||0)>visibleProcessingLimit;

  useEffect(()=>{
    setRunAccessChoice(accessStatus?.subscription?.active?'subscription':'free');
  },[Boolean(sourceSubscription),Boolean(accessStatus?.subscription?.active),initialAssessmentActive,connectionId]);

  const run=async()=>{
    setBusy('extract');setError('');
    try{
      // The model definition is always saved before any billable extraction.
      const definition=await repository.saveModel({
        id:model.id,
        name:model.name,
        category:model.category,
        processType:model.processType,
        description:model.description,
        organizationContext,
        source:{
          type:'jira-rest',
          label:connection?.name||metadata?.connection?.name||'Jira REST',
          connectionId,
          baseUrl:connection?.baseUrl||metadata?.connection?.baseUrl||'',
          jql,
          scope,
          activityFieldId,
          activityLabel:activityField?.name||'Activity',
          selectedFieldIds,
          attributeNotes,
          entityLabel:model.entityLabel||'Issue',
          businessObjective:model.businessObjective||'',
          sourceSystem:'Jira',
          retainProcessData:Boolean(retainProcessData),
          storageConsent:Boolean(retainProcessData),
        },
        sourceType:'jira-rest',
        sourceLabel:connection?.name||metadata?.connection?.name||'Jira REST',
        status:'draft',
        assessmentStatus:'not-run',
        persistence:{
          modelDefinitionSaved:true,
          retainProcessData:Boolean(retainProcessData),
          storageConsent:Boolean(retainProcessData),
          retentionMode:retainProcessData?'workspace-data':'definition-only',
          rawFileStored:false,
        },
      });

      // Access is checked before extraction. Without usable credits/subscription,
      // authorization returns a free processing limit of up to 250 cases.
      let authorization=await repository.authorizeAssessment({
        modelId:definition.id,
        modelName:model.name,
        caseCount:Number(scopeCount||0),
        sourceConnectionId:connectionId,
        processedUnitCount:Number(scopeCount||0),
        eventCount:0,
        storageRequested:Boolean(retainProcessData),
      });

      if(runAccessChoice==='assessment' && authorization?.source==='free'){
        await repository.activateAssessment(definition.id,{caseCount:Number(scopeCount||0),storageRequested:Boolean(retainProcessData)});
        authorization=await repository.authorizeAssessment({
          modelId:definition.id,modelName:model.name,caseCount:Number(scopeCount||0),sourceConnectionId:connectionId,processedUnitCount:Number(scopeCount||0),eventCount:0,storageRequested:Boolean(retainProcessData),
        });
      }

      const expectedPaidAssessment=false;
      if(expectedPaidAssessment && !['assessment','assessment_credit','subscription','workspace_subscription'].includes(String(authorization?.source||''))){
        throw new Error('This model has an activated assessment, but the processing authorization did not recognise it. No free-limit reprocess was started. Refresh Models and try again.');
      }

      const authorizedLimit=Number(authorization?.processingLimit||scopeCount||0);
      const authorizedPartial=Boolean(authorization?.partial || Number(scopeCount||0)>authorizedLimit);
      setExtractProgress({
        percent:2,
        status:'Authorized',
        step:'Starting extraction',
        detail:authorizedPartial
          ? `${fmt(authorizedLimit)} of ${fmt(scopeCount)} cases will be processed in this run.`
          : `The complete ${fmt(scopeCount)}-case scope will be processed.`
      });

      const result=await (initial?adapter.refresh:adapter.extract)({
        accessAuthorizationId:authorization?.authorizationId||null,
        retainProcessData:Boolean(retainProcessData),
        processingLimit:authorizedLimit,
        requestedCaseCount:Number(scopeCount||0),
        jql,scope,activityFieldId,selectedFieldIds,attributeNotes,
        model:{...model,organizationContext,id:definition.id,activityLabel:activityField?.name||'Activity'},
      });
      if(authorization?.source==='free' && result?.dataset?.cases?.length>authorizedLimit){
        const cases=result.dataset.cases.slice(0,authorizedLimit);
        const ids=new Set(cases.map(c=>String(c.id)));
        const events=(result.dataset.events||[]).filter(e=>ids.has(String(e.caseId)));
        result.dataset={...result.dataset,cases,events,metadata:{...(result.dataset.metadata||{}),ingestion:{...(result.dataset.metadata?.ingestion||{}),processedCaseCount:cases.length,processingLimit:authorizedLimit,partialProcessing:true,accessSource:'free'}}};
        result.validation={...(result.validation||{}),summary:{...(result.validation?.summary||{}),cases:cases.length,events:events.length,processedCases:cases.length,processingLimit:authorizedLimit,partialProcessing:true}};
      }
      if(!result?.validation?.valid){
        const first=(result?.validation?.diagnostics||[]).find(d=>d.severity==='error');
        throw new Error(first?.message||'Jira extraction failed canonical validation.');
      }
      setExtractProgress({percent:100,status:'Complete',step:'Model ready',detail:'Jira extraction completed successfully. Opening Process Advisor.'});
      await new Promise(resolve=>setTimeout(resolve,450));
      const summary=result.validation.summary||{cases:result.dataset?.cases?.length||0,events:result.dataset?.events?.length||0,variants:0};
      const runtime={
        canonicalDataset:result.dataset,
        sourceType:'jira-rest',
        diagnostics:result.validation,
        runtimeMetadata:{createdAt:new Date().toISOString(),ingestion:result.metadata||{}},
      };
      await onRunProcessAdvisor({
        id:definition.id,
        name:model.name,category:model.category,processType:model.processType,description:model.description,organizationContext,
        source:{
          type:'jira-rest',
          label:connection?.name||metadata?.connection?.name||'Jira REST',
          connectionId,
          baseUrl:connection?.baseUrl||metadata?.connection?.baseUrl||'',
          jql:result.metadata?.jql||jql,
          scope,activityFieldId,activityLabel:activityField?.name||'Activity',
          selectedFieldIds,attributeNotes,
          entityLabel:model.entityLabel||'Issue',
          businessObjective:model.businessObjective||'',
          sourceSystem:'Jira',
          retainProcessData:Boolean(retainProcessData),
          storageConsent:Boolean(retainProcessData),
        },
        sourceType:'jira-rest',
        sourceLabel:connection?.name||metadata?.connection?.name||'Jira REST',
        summary:{
          cases:summary.cases,
          events:summary.events,
          variants:Number(summary.variants||0),
          attributes:summary.caseAttributes||selectedFieldIds.length,
          configuredCases:Number(scopeCount||0),
          processedCases:Number(summary.cases||0),
          partialProcessing:Boolean(summary.partialProcessing || Number(summary.cases||0)<Number(scopeCount||0)),
          processingLimit:Number(summary.processingLimit||authorization?.processingLimit||summary.cases||0),
        },
        latestRuntime:runtime,runtime,status:'ready',assessmentStatus:'not-run',
        persistence:{
          modelDefinitionSaved:true,
          retainProcessData:Boolean(retainProcessData),
          storageConsent:Boolean(retainProcessData),
          retentionMode:retainProcessData?'workspace-data':'definition-only',
          rawFileStored:false,
        },
        accessAuthorizationId:authorization?.authorizationId||null,
        accessSource:authorization?.source||null,
        entitlement:authorization,
      });
    }catch(e){
      const message=e.message||'Unable to extract Jira model';
      setExtractProgress(prev=>({...prev,status:'Failed',step:e?.status===402?'Access required':'Extraction stopped',detail:message}));
      setError(message);
      await new Promise(resolve=>setTimeout(resolve,250));
    }finally{setBusy('');}
  };
  const stages=[
    ['scope','1','Scope'],['model','2','Model details'],['organization','3','Organization context'],['attributes','4','Activity & attributes'],['preview','5','Preview'],['run','6','Run'],
  ];
  const stageIndex=stages.findIndex(x=>x[0]===stage);
  const stepClass=i=>i<stageIndex?'done':i===stageIndex?'active':'';
  const estimateState=scopeEstimating?'loading':scopeCount==null?'unknown':scopeCount>MAX_CASES?'over':'ok';

  const previewColumns=useMemo(()=>{
    const byId=new Map((metadata?.fields||[]).map(f=>[f.id,f]));
    const attributeColumns=selectedFieldIds.map(fieldId=>{
      const field=byId.get(fieldId);
      return {
        headerName:field?.name||fieldId,
        field:`attributes.${fieldId}`,
        minWidth:150,
        flex:.8,
        valueGetter:p=>p.data?.attributes?.[fieldId]||'—',
      };
    });
    return [
      {headerName:'Key',field:'key',minWidth:125,pinned:'left',cellClass:'metricus-grid-key'},
      {headerName:'Summary',field:'summary',minWidth:250,flex:1.4,pinned:'left'},
      {headerName:'Project',field:'project',minWidth:180,flex:.9},
      {headerName:'Type',field:'issueType',minWidth:165,flex:.8},
      {headerName:`Current ${activityField?.name||'activity'}`,field:'activity',minWidth:150,flex:.8},
      ...attributeColumns,
      {headerName:'Created',field:'created',minWidth:130,valueFormatter:p=>p.value?new Intl.DateTimeFormat('en-GB',{day:'2-digit',month:'short',year:'numeric'}).format(new Date(p.value)):'—'},
    ];
  },[activityField?.name,metadata?.fields,selectedFieldIds]);

  return <div className="jira-builder jira-builder-v2">
    <div className="page-head jira-wizard-page-head">
      <div><div className="eyebrow">JIRA REST MODEL</div><h1>{initialStage==='run'?'Reprocess Jira model':initial?'Change Jira model configuration':'New Jira model'}</h1><p>{initialStage==='run'?'Process the saved model again using its current data configuration.':'Configure the model in six short steps. The model definition is saved independently of data retention.'}</p></div>
      <button className="pa-secondary" onClick={onBack}><ArrowLeft size={15}/> {initial?'Back to models':'Change connection'}</button>
    </div>

    <div className="jira-stage-strip">
      {stages.map((s,i)=><React.Fragment key={s[0]}>
        <div className={`jira-stage ${stepClass(i)}`}><span>{i<stageIndex?<Check size={14}/>:s[1]}</span><b>{s[2]}</b></div>
        {i<stages.length-1&&<div className={`jira-stage-line ${i<stageIndex?'done':''}`}/>}
      </React.Fragment>)}
    </div>

    {error&&<div className="pa-alert error jira-wizard-alert">{error}</div>}
    {busy==='metadata'&&!metadata&&<div className="panel"><div className="rest-loading"><Loader2 className="spin" size={24}/><b>Loading Jira projects, issue types and fields…</b></div></div>}

    {stage==='scope'&&metadata&&<section className="panel jira-scope-panel-v3">
      <div className="jira-scope-head-v3">
        <div>
          <h2>Define scope</h2>
          <p>Select the Jira projects and issue types to include in this process model.</p>
        </div>
        <div className="jira-scope-head-right">
          <div className="source-context-chip v3"><Database size={16}/>{metadata.connection?.name||connection?.name}</div>
          <div className={`jira-scope-count-v3 ${estimateState}`}>
            <span>Cases in scope</span>
            <strong>{scopeEstimating?'…':scopeCount==null?'—':fmt(scopeCount)}</strong>
            <small>{scopeEstimateError
              ? scopeEstimateError
              : scopeCount==null
                ? `Maximum ${fmt(MAX_CASES)}`
                : scopeCount>MAX_CASES
                  ? `${fmt(scopeCount-MAX_CASES)} over limit`
                  : `of ${fmt(MAX_CASES)} maximum`}</small>
          </div>
        </div>
      </div>

      <div className="jira-scope-body-v3">
        <div className="jira-scope-select-grid-v3">
          <MetricusMultiSelect
            label="Projects"
            options={projectOptions}
            value={selectedProjectOptions}
            placeholder="All projects"
            helperText={scope.projectKeys.length
              ? `${scope.projectKeys.length} of ${projects.length} projects selected`
              : `All ${projects.length} projects included`}
            onChange={items=>updateScope({...scope,projectKeys:(items||[]).map(item=>item.value)})}
          />

          <MetricusMultiSelect
            label="Issue types"
            options={issueTypeOptions}
            value={selectedIssueTypeOptions}
            placeholder="All issue types"
            helperText={scope.issueTypeNames.length
              ? `${scope.issueTypeNames.length} of ${issueTypes.length} issue types selected`
              : `All ${issueTypes.length} issue types included`}
            onChange={items=>updateScope({...scope,issueTypeNames:(items||[]).map(item=>item.value)})}
          />
        </div>

        <button
          type="button"
          className={`jira-advanced-toggle-v3 ${showAdvancedScope?'open':''}`}
          onClick={()=>setShowAdvancedScope(value=>!value)}
          aria-expanded={showAdvancedScope}
        >
          <span>Additional filters</span>
          <small>Date range or JQL</small>
          <ChevronDown size={17}/>
        </button>

        {showAdvancedScope&&<div className="jira-advanced-scope-v3">
          <label>
            <span>Created from</span>
            <input type="date" value={scope.fromDate} onChange={e=>updateScope({...scope,fromDate:e.target.value})}/>
          </label>
          <label>
            <span>Created to</span>
            <input type="date" value={scope.toDate} onChange={e=>updateScope({...scope,toDate:e.target.value})}/>
          </label>
          <label className="jira-advanced-jql-v3">
            <span>Additional JQL <em>optional</em></span>
            <input value={scope.additionalJql} onChange={e=>updateScope({...scope,additionalJql:e.target.value})} placeholder='e.g. priority in (Highest, High)'/>
          </label>
        </div>}
      </div>

      <div className="jira-scope-footer-v3">
        <button className="pa-secondary" onClick={onBack}>Back</button>
        <button
          className="pa-primary"
          disabled={!!busy||scopeEstimating||scopeCount==null||scopeCount===0||scopeCount>MAX_CASES}
          onClick={validateScope}
        >
          {busy==='count'?'Validating…':'Validate & continue'}<ChevronRight size={16}/>
        </button>
      </div>
    </section>}
    {stage==='model'&&<section className="panel jira-wizard-panel metricus-wizard-panel">
      <div className="metricus-panel-head">
        <div><h2>Model details</h2><p>Name the process and give Advisor enough business context to interpret the evidence.</p></div>
      </div>
      <div className="metricus-panel-body">
        <div className="jira-validation-banner metricus-validation-banner"><CheckCircle2 size={20}/><div><b>Scope validated</b><span>{fmt(scopeCount)} Jira cases</span></div><button type="button" onClick={()=>setStage('scope')}>Edit scope</button></div>
        <div className="metricus-form-grid two">
          <MetricusInput label="Model name *" value={model.name} onChange={e=>setModel({...model,name:e.target.value})}/>
          <MetricusInput label="Process *" value={model.processType} onChange={e=>setModel({...model,processType:e.target.value})} placeholder="e.g. Incident Management"/>
          <MetricusInput label="Category" value={model.category} onChange={e=>setModel({...model,category:e.target.value})} placeholder="e.g. ITSM"/>
          <MetricusInput label="Case / entity name" value={model.entityLabel} onChange={e=>setModel({...model,entityLabel:e.target.value})} placeholder="Issue"/>
          <MetricusInput className="metricus-span-two" label="Description / scope" multiline rows={2} value={model.description} onChange={e=>setModel({...model,description:e.target.value})} placeholder="Optional process context"/>
        </div>
      </div>
      <div className="metricus-panel-footer"><button className="pa-secondary" onClick={()=>setStage('scope')}>Back</button><button className="pa-primary" onClick={toOrganization}>Continue<ChevronRight size={15}/></button></div>
    </section>}

    {stage==='organization'&&<OrganizationContextStep
      value={organizationContext}
      onChange={setOrganizationContext}
      repository={repository}
      sourceSystem="Jira"
      isExisting={Boolean(initial?.id)}
      modelContext={{name:model.name,category:model.category,processType:model.processType,description:model.description,entityLabel:model.entityLabel}}
      onBack={()=>setStage('model')}
      onContinue={()=>setStage('attributes')}
    />}

    {stage==='attributes'&&metadata&&<section className="panel jira-wizard-panel metricus-wizard-panel">
      <div className="metricus-panel-head"><div><h2>Activity & attributes</h2><p>Choose the activity that defines the process sequence and the case characteristics used by Segments and AI.</p></div></div>
      <div className="metricus-panel-body metricus-attributes-body">
        <div className="metricus-activity-card">
          <MetricusSelect
            label="Activity field"
            value={activityFieldId}
            onChange={e=>setActivityFieldId(e.target.value)}
            options={(metadata.fields||[]).map(f=>({value:f.id,label:`${f.name}${f.custom?' · custom':''}`}))}
          />
          <div className="metricus-activity-copy"><b>{activityField?.name||'Activity'} drives the process map</b><span>Process Advisor reconstructs the ordered history of this Jira field.</span></div>
        </div>

        <div className="metricus-characteristics-card metricus-characteristics-card-v2">
          <div className="metricus-characteristics-head">
            <div>
              <h3>Selected characteristics</h3>
              <p>{selectedFieldIds.length} selected{!initial?.source?.selectedFieldIds?.length?' · recommended defaults applied':''}</p>
            </div>
          </div>

          {selectedAttributeFields.length
            ? <div className="metricus-selected-attribute-grid">
                {selectedAttributeFields.map(f=><div className="metricus-selected-attribute-row" key={f.id}>
                  <div className="metricus-selected-attribute-name">
                    <span><b>{f.name}</b><small>{f.id}{f.custom?' · custom':''}</small></span>
                    <button type="button" onClick={()=>toggleAttribute(f.id)} title={`Remove ${f.name}`} aria-label={`Remove ${f.name}`}><X size={15}/></button>
                  </div>
                  <MetricusInput
                    value={attributeNotes[f.id]||''}
                    onChange={e=>setAttributeNotes({...attributeNotes,[f.id]:e.target.value})}
                    placeholder="Business meaning for AI (optional)"
                  />
                </div>)}
              </div>
            : <div className="metricus-selected-empty">No characteristics selected.</div>}
        </div>

        <div className="metricus-available-fields">
          <div className="metricus-available-fields-head">
            <div><h3>Add characteristics</h3><p>Select additional Jira fields to use for Segments and AI.</p></div>
            <span>{selectedFieldIds.length} selected</span>
          </div>

          <div className="metricus-attribute-toolbar">
            <div className="metricus-search"><Search size={16}/><input value={fieldSearch} onChange={e=>setFieldSearch(e.target.value)} placeholder="Search Jira fields"/></div>
            <span>{(metadata.fields||[]).filter(f=>!CORE_FIELD_IDS.has(f.id)&&f.id!==activityFieldId).length} available</span>
          </div>

          <div className="metricus-available-field-grid">
            {(metadata.fields||[])
              .filter(f=>!CORE_FIELD_IDS.has(f.id)&&f.id!==activityFieldId)
              .filter(f=>{
                const q=fieldSearch.trim().toLowerCase();
                return !q||`${f.name||''} ${f.id||''}`.toLowerCase().includes(q);
              })
              .sort((a,b)=>{
                const sa=selectedFieldIds.includes(a.id)?0:1;
                const sb=selectedFieldIds.includes(b.id)?0:1;
                return sa-sb||String(a.name).localeCompare(String(b.name));
              })
              .map(f=>{
                const checked=selectedFieldIds.includes(f.id);
                return <label className={`metricus-available-field ${checked?'selected':''}`} key={f.id}>
                  <input type="checkbox" checked={checked} onChange={()=>toggleAttribute(f.id)}/>
                  <span><b>{f.name}</b><small>{f.id}{f.custom?' · custom':''}</small></span>
                </label>;
              })}
          </div>
        </div>
      </div>
      <div className="metricus-panel-footer"><button className="pa-secondary" onClick={()=>setStage('organization')}>Back</button><div className="metricus-footer-actions"><span>{selectedFieldIds.length} characteristics selected</span><button className="pa-primary" disabled={!!busy} onClick={previewModel}>{busy==='preview'?'Loading preview…':'Preview data'}<ChevronRight size={15}/></button></div></div>
    </section>}
    {stage==='preview'&&<section className="panel jira-wizard-panel metricus-wizard-panel metricus-preview-panel-v2">
      <div className="metricus-preview-toolbar-v2">
        <div>
          <h2>Preview data</h2>
          <span>{fmt(scopeCount)} cases · Activity: {activityField?.name||'Activity'} · {selectedFieldIds.length} characteristics · {connection?.name||metadata?.connection?.name||'Jira REST'}</span>
        </div>
        <span className="metricus-preview-note-v2">Sample rows before full extraction</span>
      </div>
      <div className="metricus-preview-grid-v2">
        <MetricusDataGrid rowData={preview} columnDefs={previewColumns} height={Math.min(610,Math.max(360,44*(preview.length||1)+42))} emptyMessage="No preview rows returned"/>
      </div>
      <div className="metricus-panel-footer"><button className="pa-secondary" onClick={()=>setStage('attributes')}>Back</button><button className="pa-primary" disabled={!!busy} onClick={()=>setStage('run')}>Continue<ChevronRight size={15}/></button></div>
    </section>}
    {stage==='run'&&<section className="panel jira-wizard-panel metricus-wizard-panel metricus-run-panel-v2">
      <div className="metricus-run-head-v2">
        <div>
          <h2>{initial?'Refresh Process Advisor':'Run Process Advisor'}</h2>
          <p>Confirm the processing allowance and choose whether Process Advisor may retain the processed data.</p>
        </div>
      </div>

      <div className="metricus-run-body-v2">
        <div className="metricus-run-model-v2">
          <div className="metricus-run-model-title">
            <Database size={21}/>
            <div>
              <b>{model.name}</b>
              <span>{model.processType||'Process'} · {connection?.name||metadata?.connection?.name||'Jira'}</span>
            </div>
          </div>
          <div className="metricus-run-model-meta">
            <span>Activity</span><b>{activityField?.name||'Activity'}</b>
          </div>
        </div>

        <div className="metricus-processing-summary-v2">
          <div>
            <span>Cases in configured scope</span>
            <strong>{fmt(scopeCount)}</strong>
          </div>
          <div>
            <span>Available this run</span>
            <strong>{fmt(visibleProcessingLimit)}</strong>
          </div>
          <div>
            <span>Access</span>
            <strong>{paidBySubscription?'Full access':'Free trial'}</strong>
          </div>
        </div>



        {partialRun
          ? <div className="metricus-partial-run-v2">
              <div>
                <b>{fmt(visibleProcessingLimit)} of {fmt(scopeCount)} cases will be processed.</b>
                <span>The model definition still represents the full configured scope. Subscribe for full access to process all {fmt(scopeCount)} cases.</span>
              </div>
            </div>
          : <div className="metricus-full-run-v2">
              <CheckCircle2 size={18}/>
              <span>The complete {fmt(scopeCount)}-case scope will be processed.</span>
            </div>}

        <label className={`metricus-retention-card-v2 ${retainProcessData?'selected':''}`}>
          <input type="checkbox" checked={retainProcessData} onChange={e=>setRetainProcessData(e.target.checked)}/>
          <span>
            <b>Save processed data</b>
            <small>Save the cases, events and selected characteristics processed in this run so the model can reopen without extracting them again. The model definition is always saved.</small>
          </span>
          <em>{retainProcessData
            ? `Will save ${fmt(visibleProcessingLimit)} processed ${visibleProcessingLimit===1?'case':'cases'}`
            : 'Processed data will not be retained'}</em>
        </label>

        <div className="metricus-usage-note-v2">
          <b>Processing allowance</b>
          <span>Usage is based on the number of cases processed. Saving or discarding the processed data does not change usage.</span>
        </div>
      </div>

      <div className="metricus-panel-footer">
        <button className="pa-secondary" disabled={!!busy} onClick={()=>setStage('preview')}>Back</button>
        <div className="metricus-footer-actions">
          <span>Model definition is always saved</span>
          <button className="pa-primary" disabled={!!busy} onClick={run}>{initial?'Refresh & run':'Extract & run'}</button>
        </div>
      </div>
    </section>}
    {busy==='extract'&&<div className="jira-extraction-overlay" role="dialog" aria-modal="true" aria-label="Jira model extraction progress">
      <div className="jira-extraction-dialog">
        <div className="jira-extraction-top">
          <div><span>PROCESS ADVISOR</span><h2>{extractProgress.percent<100?'Preparing model extraction':'Model ready'}</h2><p>{extractProgress.detail}</p></div>
          <strong>{extractProgress.percent}%</strong>
        </div>
        <div className="jira-progress-track"><div className="jira-progress-fill" style={{width:`${extractProgress.percent}%`}}/></div>
        <div className="jira-extraction-stats">
          <div><span>STATUS</span><b>{extractProgress.status}</b></div>
          <div><span>STEP</span><b>{extractProgress.step}</b></div>
        </div>
        <div className="jira-extraction-model"><Database size={18}/><div><b>{model.name}</b><span>{connection?.name||metadata?.connection?.name} · processing up to {fmt(visibleProcessingLimit)} of {fmt(scopeCount)} cases</span></div></div>
        <div className="jira-extraction-note">Process Advisor is retrieving Jira data and preparing the canonical process model. The dialog closes when the model is ready.</div>
      </div>
    </div>}
  </div>;
}
